/**
 * usePublicStats — fetches the public aggregate landing stats once and shares the
 * result across all consumers via a module-level cache (plan §4.7 / §5).
 *
 * Source of truth: GET /api/public/landing-stats (newbackend/src/routes/publicStats.js),
 * an UNAUTHENTICATED, aggregate-only endpoint. The response shape mirrored below is the
 * exact contract that route returns.
 *
 * Seed fallback (SEED_STATS) is intentionally a set of ROUND placeholders (records 20000,
 * participants 80, days 1500) — NOT the real counts. They render immediately and on any
 * fetch failure, so a wrong-looking round number is the deliberate "the endpoint didn't
 * load" tell. The live values replace them when the fetch resolves (a brief, expected jump).
 */

import { useEffect, useState } from 'react';

/** Per-study aggregate count, labelled by institution only (never study_code/name). */
export interface PerStudySampleCount {
  label: string;
  count: number;
}

/** Exact response contract of GET /api/public/landing-stats. */
export interface PublicStats {
  totals: {
    records: number;
    participants: number;
  };
  span: {
    days: number;
  };
  // Always empty today, but kept in the contract: the studies-map config documents this
  // as its client-side count-join source (see mapConfig.ts). No DB work to produce it.
  perStudySampleCounts: PerStudySampleCount[];
}

/**
 * Seed constants — shown immediately and on any fetch failure so the UI never flashes
 * zeros/blanks. The figures are intentionally ROUND placeholders, NOT real counts, so a
 * failed fetch is obvious at a glance.
 */
export const SEED_STATS: PublicStats = {
  totals: {
    // Intentionally ROUND (not a real count) so it's obvious this is the offline
    // fallback, not a live fetch — seeing exactly 20,000 means the endpoint didn't load.
    records: 20000,
    participants: 80, // round placeholder (not real) — seeing exactly 80 means the fetch didn't load
  },
  span: {
    days: 1500, // round placeholder (not real) — seeing exactly 1,500 means the fetch didn't load
  },
  perStudySampleCounts: [],
};

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002';
const ENDPOINT = `${API_BASE}/api/public/landing-stats`;

export interface UsePublicStatsResult {
  data: PublicStats;
  loading: boolean;
  error: Error | null;
}

// Module-level cache so the endpoint is hit at most once per page load, regardless of how
// many components mount the hook. The in-flight promise is shared so concurrent mounts do
// not each fire a request.
let cachedStats: PublicStats | null = null;
let inFlight: Promise<PublicStats> | null = null;

function fetchStats(): Promise<PublicStats> {
  if (cachedStats) return Promise.resolve(cachedStats);
  if (inFlight) return inFlight;

  inFlight = fetch(ENDPOINT, { headers: { Accept: 'application/json' } })
    .then((res) => {
      if (!res.ok) {
        throw new Error(`landing-stats responded ${res.status}`);
      }
      return res.json() as Promise<PublicStats>;
    })
    .then((json) => {
      cachedStats = json;
      return json;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

export function usePublicStats(): UsePublicStatsResult {
  // Seed immediately so consumers always render real-looking numbers (no 0→N flash).
  const [data, setData] = useState<PublicStats>(cachedStats || SEED_STATS);
  const [loading, setLoading] = useState<boolean>(cachedStats === null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (cachedStats) {
      setData(cachedStats);
      setLoading(false);
      return;
    }

    fetchStats()
      .then((stats) => {
        if (cancelled) return;
        setData(stats);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Keep the seed data on failure — the UI must never show zeros/blanks (plan §4.7 risks).
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { data, loading, error };
}

// Test/dev helper: clear the module-level cache so a fresh fetch occurs.
export function _resetPublicStatsCache(): void {
  cachedStats = null;
  inFlight = null;
}

export default usePublicStats;
